import { MouseEvent, FocusEventHandler, ReactNode, JSX, Ref } from 'react';

import classNames from 'classnames';
import MUIIconButton, { IconButtonClassKey } from '@mui/material/IconButton';
import { ButtonBaseActions, TouchRippleProps } from '@mui/material/ButtonBase';
import { ClassNameMap } from '@mui/material/styles';

import useStyles from './IconButton.styles';

/**
 * Цвет кнопки
 */
export type Color = 'inherit' | 'primary' | 'secondary' | 'default';

/**
 * Свойства компонента {@link IconButton}
 */
export type IconButtonProps = JSX.IntrinsicAttributes & {
  /**
   * Цвет кнопки.
   */
  color?: Color;

  /**
   * Если true, эффект всплеска при фокусировке с клавиатуры будет отключен.
   *
   * @defaultValue `false`
   */
  disableFocusRipple?: boolean;

  /**
   * Если указано, применяется небольшой фикс для выравнивания содержимого по началу или концу.
   */
  edge?: false | 'end' | 'start';

  /**
   * Размер кнопки.
   *
   * @defaultValue `'medium'`
   */
  size?: 'small' | 'medium' | 'extraSmall';

  /**
   * Компонент, используемый для корневого узла.
   */
  component?: 'span';

  /**
   * Переопределяет или расширяет стили, применяемые к компоненту.
   */
  className?: string;

  /**
   * Коллбэк, вызываемый при клике на кнопку.
   */
  onClick?: (e: MouseEvent<HTMLElement>) => void;

  /**
   * Если true, снимает фокус с активного элемента после клика.
   *
   * @defaultValue `true`
   */
  blurOnClick?: boolean;

  /**
   * Тип кнопки.
   *
   * @defaultValue `'button'`
   */
  type?: 'button';

  /**
   * Переопределяет или расширяет стили, применяемые к компоненту.
   */
  classes?: Partial<ClassNameMap<IconButtonClassKey>>;

  /**
   * Идентификатор для тестирования.
   *
   * @defaultValue `'icon-button-test-id'`
   */
  testId?: string;

  /**
   * HTML-элемент или функция, возвращающая один.
   */
  action?: Ref<ButtonBaseActions>;

  /**
   * Ссылка, прикрепленная к элементу кнопки.
   */
  buttonRef?: Ref<unknown>;

  /**
   * Если true, эффект всплеска начинается по центру кнопки.
   *
   * @defaultValue `false`
   */
  centerRipple?: boolean;

  /**
   * Содержимое кнопки.
   */
  children?: ReactNode;

  /**
   * Если true, кнопка будет отключена.
   *
   * @defaultValue `false`
   */
  disabled?: boolean;

  /**
   * Если true, эффект всплеска будет отключен.
   *
   * @defaultValue `false`
   */
  disableRipple?: boolean;

  /**
   * Если true, эффект всплеска при касании будет отключен.
   *
   * @defaultValue `false`
   */
  disableTouchRipple?: boolean;

  /**
   * Если true, кнопка будет показывать эффект всплеска при фокусировке.
   *
   * @defaultValue `false`
   */
  focusRipple?: boolean;

  /**
   * Переопределяет или расширяет стили, применяемые к кнопке с видимым фокусом.
   */
  focusVisibleClassName?: string;

  /**
   * Коллбэк, вызываемый при фокусировке кнопки с клавиатуры.
   */
  onFocusVisible?: FocusEventHandler;

  /**
   * Атрибут tabindex.
   */
  tabIndex?: number;

  /**
   * Свойства, применяемые к элементу `TouchRipple`.
   */
  TouchRippleProps?: Partial<TouchRippleProps>;
};

/**
 * Пользовательский компонент IconButton, который расширяет компонент IconButton из Material-UI.
 * Он включает дополнительную функциональность для снятия фокуса с активного элемента при клике.
 *
 * @public
 * @param color - Цвет кнопки.
 * @param disableFocusRipple - Если true, эффект всплеска при фокусировке с клавиатуры будет отключен.
 * @param edge - Если указано, применяется небольшой фикс для выравнивания содержимого по началу или концу.
 * @param size - Размер кнопки.
 * @param component - Компонент, используемый для корневого узла.
 * @param className - Переопределяет или расширяет стили, применяемые к компоненту.
 * @param onClick - Коллбэк, вызываемый при клике на кнопку.
 * @param blurOnClick - Если true, снимает фокус с активного элемента после клика.
 * @param type - Тип кнопки.
 * @param classes - Переопределяет или расширяет стили, применяемые к компоненту.
 * @param testId - Идентификатор для тестирования.
 * @param action - HTML-элемент или функция, возвращающая один.
 * @param buttonRef - Ссылка, прикрепленная к элементу кнопки.
 * @param centerRipple - Если true, эффект всплеска начинается по центру кнопки.
 * @param children - Содержимое кнопки.
 * @param disabled - Если true, кнопка будет отключена.
 * @param disableRipple - Если true, эффект всплеска будет отключен.
 * @param disableTouchRipple - Если true, эффект всплеска при касании будет отключен.
 * @param focusRipple - Если true, кнопка будет показывать эффект всплеска при фокусировке.
 * @param focusVisibleClassName - Переопределяет или расширяет стили, применяемые к кнопке с видимым фокусом.
 * @param onFocusVisible - Коллбэк, вызываемый при фокусировке кнопки с клавиатуры.
 * @param tabIndex - Атрибут tabindex.
 * @param TouchRippleProps - Свойства, применяемые к элементу `TouchRipple`.
 * @returns  JSX-элемент, представляющий кнопку IconButton.
 *
 * @remarks
 *
 * Ссылка на свойства компонента {@link IconButtonProps}
 */
export const IconButton = (props: IconButtonProps) => {
  const {
    onClick,
    blurOnClick = true,
    testId = 'icon-button-test-id',
    size,
    ...rest
  } = props;

  const { classes } = useStyles();

  const handleClick = (e: MouseEvent<HTMLElement>) => {
    if (blurOnClick && document.activeElement instanceof HTMLElement) {
      document.activeElement.blur();
    }

    onClick?.(e);
  };

  return (
    <MUIIconButton
      {...rest}
      type="button"
      onClick={handleClick}
      className={classNames(
        props.className
          ? `${props.className} aqa_icon_button`
          : 'aqa_icon_button',
        size === 'extraSmall' && classes.extraSmall,
      )}
      size={size === 'extraSmall' ? 'small' : size}
      data-testid={testId}
    >
      {props.children}
    </MUIIconButton>
  );
};
